import React from "react"
import {
    JobStatus,
    APIJobQueryModel,
    APIJobModel,
    isJobFeedbackModel
} from "../../../../../../Models/API/JobModel"
import { SortOptionField } from "../../../../../Common/Button/SortChooser"
import {
    FilterFields,
    dateTimeFilter,
    enumFilter,
    numberFilter,
    stringFilter
} from "../../../../../Common/FilterComposer/FilterComposer"
import startOfDay from "date-fns/startOfDay"
import {
    ActionsFilterOperator,
    FeedbackMessageFilterOperator,
    FilterExpression,
    MultipleValueFilterOperator,
    UnaryFilterOperator
} from "../../../../../../Models/Client/FilterExpression"
import { APIGroundTruthModel } from "../../../../../../Models/API/APIGroundTruthModel"
import { FilterableJsonField } from "../../../../../../Models/Client/JsonFieldFilter"
import { JsonPredicateInput } from "../JobsList/JsonPredicateInput"
import {
    APIFeedbackTag,
    FeedbackChoices,
    FeedbackMessageViewState
} from "../../../../../../Models/API/JobFeedbackModel"
import { ChatTagProps, MultipleChoiceDropdown } from "dash-styled"
import {
    FeedbackMessageInput,
    FeedbackMessageTagInput
} from "../JobsList/FeedbackMessagePredicateInput"
import {
    FetchState,
    getValue,
    hasValue
} from "../../../../../../Models/Client/FetchState"
import { PredicateInputComponentProps } from "../../../../../Common/FilterComposer/FilterPredicate"

const defaultDateOnAddFilter = startOfDay(new Date()).toISOString()

export const jobSortOptions: SortOptionField<APIJobQueryModel>[] = [
    {
        field: "id",
        label: "ID",
        ascLabel: "Lowest first",
        descLabel: "Highest first"
    },
    {
        field: "created",
        label: "Created",
        ascLabel: "Oldest first",
        descLabel: "Youngest first"
    },
    {
        field: "status",
        label: "Status",
        ascLabel: "Completed last",
        descLabel: "Completed first"
    },
    {
        field: "uuid",
        label: "Random"
    },
    {
        field: "num_pages",
        label: "Number of Pages"
    }
]

export const modelScoreSortOptions: SortOptionField<APIJobQueryModel> = {
    field: "score",
    label: "Model confidence",
    ascLabel: "Lowest first",
    descLabel: "Highest first"
}

export const jobWithMessagesSortOptions: SortOptionField<APIJobQueryModel>[] = [
    ...jobSortOptions,
    {
        field: "fmcreated",
        label: "Messages",
        ascLabel: "Recent last",
        descLabel: "Recent first"
    }
]

export const defaultJobFilterOnAdd: FilterExpression<APIJobQueryModel> = {
    fieldName: "status",
    operator: MultipleValueFilterOperator.isOneOf,
    value: []
}

export const jobFilterOptions = (
    tagsFetch: FetchState<string[]>,
    inputFields: FilterableJsonField[],
    outputFields: FilterableJsonField[],
    metadataSearch: boolean,
    onSearchTags: (keyword: string) => void,
    isMessages?: boolean,
    feedbackTags?: APIFeedbackTag[],
    showScoreFilters?: boolean,
    isCanoticCrowd?: boolean
): FilterFields<APIJobQueryModel & { output: unknown }> => ({
    id: numberFilter<string>("ID", {
        toNumber: parseFloat,
        fromNumber: v => String(v)
    }),
    completed: dateTimeFilter("Completed", defaultDateOnAddFilter),
    created: dateTimeFilter("Created", defaultDateOnAddFilter),
    status: enumFilter<JobStatus>("Processing status", {
        Parked: JobStatus.PENDING,
        Queued: JobStatus.SCHEDULED,
        [isCanoticCrowd ? "In Progress" : "Ready to process"]:
            JobStatus.IN_PROGRESS,
        Paused: JobStatus.SUSPENDED,
        Complete: JobStatus.COMPLETED,
        Canceled: JobStatus.CANCELED,
        Failed: JobStatus.FAILED,
        Expired: JobStatus.EXPIRED
    }),
    correct: enumFilter("Review status", {
        Correct: true,
        Incorrect: false,
        "Not Reviewed": null
    }),
    batchId: stringFilter<string>(
        "Batch",
        v => v,
        v => v,
        "",
        UnaryFilterOperator.eq,
        "batch ID is"
    ),
    tags: {
        label: "Tags",
        allowMultiple: false,
        predicates: [
            {
                label: "contain one of",
                operator: MultipleValueFilterOperator.isOneOf,
                defaultValue: [],
                component: createTagsFilterComponent(tagsFetch, onSearchTags)
            },
            {
                label: "do not contain",
                operator: MultipleValueFilterOperator.isNotOneOf,
                defaultValue: [],
                component: createTagsFilterComponent(tagsFetch, onSearchTags)
            }
        ]
    },
    ...(inputFields.length > 0
        ? {
              input: {
                  label: "Input data",
                  allowMultiple: false,
                  predicates: [
                      {
                          label: "matches",
                          operator: UnaryFilterOperator.contains,
                          defaultValue: {
                              path: inputFields[0].path,
                              matchType: "CONTAINS",
                              value: ""
                          },
                          component: props => (
                              <JsonPredicateInput
                                  {...props}
                                  fields={inputFields}
                              />
                          )
                      }
                  ]
              }
          }
        : {}),
    ...(outputFields.length > 0
        ? {
              response: {
                  label: "Output data",
                  allowMultiple: false,
                  predicates: [
                      {
                          label: "matches",
                          operator: UnaryFilterOperator.contains,
                          defaultValue: {
                              path: outputFields[0].path,
                              matchType: "CONTAINS",
                              value: ""
                          },
                          component: ({ value, onChange }) => (
                              <JsonPredicateInput
                                  value={value}
                                  onChange={onChange}
                                  fields={outputFields}
                              />
                          )
                      }
                  ]
              }
          }
        : {}),
    ...(metadataSearch
        ? {
              metadata: stringFilter<{ [key: string]: unknown } | null>(
                  "Metadata",
                  v =>
                      v !== null &&
                      "search" in v &&
                      typeof v.search === "string"
                          ? v.search
                          : "",
                  v => ({ search: v }),
                  { search: "" },
                  UnaryFilterOperator.contains,
                  "contains"
              )
          }
        : {}),
    messages: {
        label: "Messages",
        allowMultiple: false,
        predicates: [
            ...(!isMessages
                ? [
                      {
                          label: "has message",
                          operator: FeedbackMessageFilterOperator.isMessage,
                          defaultValue: {
                              onlyIfFeedback: true
                          }
                      }
                  ]
                : []),
            ...(feedbackTags && feedbackTags.length > 0
                ? [
                      {
                          label: "tags contain",
                          operator: FeedbackMessageFilterOperator.isOneOfTag,
                          defaultValue: {
                              feedbackTags: feedbackTags[0].id
                          },
                          component: FeedbackMessageTagInput(
                              feedbackTags.reduce(
                                  (passed, t) => ({ ...passed, [t.tag]: t.id }),
                                  {} as { [key: string]: number }
                              ),
                              (a, b) => JSON.stringify(a) === JSON.stringify(b)
                          )
                      }
                  ]
                : []),
            {
                label: "text contains",
                operator: FeedbackMessageFilterOperator.isMessageContains,
                defaultValue: {
                    feedbackSearch: ""
                },
                component: FeedbackMessageInput()
            }
        ]
    },
    actions: {
        label: "Actions",
        allowMultiple: true,
        predicates: [
            {
                label: "originally marked incorrect",
                operator: ActionsFilterOperator.firstIncorrect,
                defaultValue: {
                    firstIncorrect: true
                }
            },
            {
                label: "output reprocessed",
                operator: ActionsFilterOperator.relabeled,
                defaultValue: {
                    relabeled: true
                }
            },
            {
                label: "output edited",
                operator: ActionsFilterOperator.edited,
                defaultValue: {
                    edited: true
                }
            }
        ]
    },
    ...(showScoreFilters
        ? {
              score: numberFilter<number | null>("Model confidence", {
                  toNumber: v => v,
                  fromNumber: v => v
              })
          }
        : {})
})

export const groundTruthSortOptions: SortOptionField<APIGroundTruthModel>[] = [
    {
        field: "id",
        label: "Data point ID",
        ascLabel: "Lowest first",
        descLabel: "Highest first"
    },
    {
        field: "created",
        label: "Created",
        ascLabel: "Oldest first",
        descLabel: "Youngest first"
    },
    {
        field: "used",
        label: "Measurements"
    },
    { field: "type", label: "Source" }
]

// this color is taken from styled/ChatTagProps
const feedbackTagByPositionColorMap = new Map<
    number,
    ChatTagProps["chipColor"]
>()
feedbackTagByPositionColorMap.set(0, "cream")
feedbackTagByPositionColorMap.set(1, "lightpink")
feedbackTagByPositionColorMap.set(2, "orange")
feedbackTagByPositionColorMap.set(3, "purple")
feedbackTagByPositionColorMap.set(4, "cyan")
feedbackTagByPositionColorMap.set(5, "green")
feedbackTagByPositionColorMap.set(6, "dirtygreen")
feedbackTagByPositionColorMap.set(7, "pink")
feedbackTagByPositionColorMap.set(8, "orangishyellow")
feedbackTagByPositionColorMap.set(-2, "blue")

const feedbackTagColorMap = new Map<string, ChatTagProps["chipColor"]>()
feedbackTagColorMap.set(FeedbackChoices.NOT_SURE, "cream")
feedbackTagColorMap.set(FeedbackChoices.NOT_SURE_NEW, "cream")
feedbackTagColorMap.set(FeedbackChoices.INPUT_DATA_PROBLEM, "lightpink")
feedbackTagColorMap.set(FeedbackChoices.INPUT_DATA_PROBLEM_NEW, "lightpink")
feedbackTagColorMap.set(FeedbackChoices.TOO_LONG, "orange")
feedbackTagColorMap.set(FeedbackChoices.TOO_LONG_NEW, "orange")
feedbackTagColorMap.set(FeedbackChoices.LABELING_PROBLEM, "purple")
feedbackTagColorMap.set(FeedbackChoices.LABELING_PROBLEM_NEW, "purple")
feedbackTagColorMap.set(FeedbackChoices.SOMETHING_ELSE, "blue")
feedbackTagColorMap.set(FeedbackChoices.SOMETHING_ELSE_NEW, "blue")

export const getFeedbackTagColor = (
    tag?: APIFeedbackTag | string,
    appTags?: APIFeedbackTag[]
):
    | "violet"
    | "lightpink"
    | "cream"
    | "orange"
    | "purple"
    | "cyan"
    | "green"
    | "pink"
    | "orangishyellow"
    | "dirtygreen"
    | "blue"
    | "gray"
    | "blueviolet"
    | undefined => {
    if (typeof tag === "string") {
        return feedbackTagColorMap.get(tag) ?? "gray"
    } else if (tag && tag.parentId && appTags) {
        const parentTag = appTags.find(
            parentTag => parentTag.id === tag.parentId
        )
        if (parentTag) {
            return (
                feedbackTagByPositionColorMap.get(parentTag.position) ?? "gray"
            )
        }
    } else if (tag) {
        return feedbackTagByPositionColorMap.get(tag.position) ?? "gray"
    } else {
        return "gray"
    }
}

export function unreadMessageClass(item: APIJobModel) {
    if (isJobFeedbackModel(item)) {
        return item.feedbackMessage?.viewState !==
            FeedbackMessageViewState.UNREAD
            ? "bg-marketplace-bg-grey"
            : "bg-white"
    }
    return ""
}

function createTagsFilterComponent(
    tagsFetch: FetchState<string[]>,
    onSearchTags: (keyword: string) => void
) {
    return ({ value, onChange }: PredicateInputComponentProps<string[]>) => {
        const choices = getValue(tagsFetch) ?? []
        const handleChange = (chosen: string) =>
            onChange(
                value.includes(chosen)
                    ? value.filter(v => v !== chosen)
                    : value.concat([chosen])
            )

        return (
            <MultipleChoiceDropdown
                className="flex-grow"
                testId="filter-value"
                choices={choices}
                values={value}
                onClick={handleChange}
                size="sm"
                isLoadingChoices={!hasValue(tagsFetch)}
                onSearch={onSearchTags}
            />
        )
    }
}

export const jsonAndFormattedOutputFormats = ["formatted", "json"] as const
export const possibleOutputFormats = [
    "processed",
    ...jsonAndFormattedOutputFormats
] as const
